import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, Repeat, Lock, Info, List, CalendarDays, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { apiPersons, apiTimeOffs, apiTimeOffTypes, apiPublicHolidays, apiSettings, apiSchedule } from '../api/client.js';
import Modal from '../components/Modal.jsx';
import DatePicker from '../components/DatePicker.jsx';
import HalfDayPicker, { timeOffDurationLabel, gescomRequestUrl } from '../components/HalfDayPicker.jsx';
import EditScopeDialog from '../components/EditScopeDialog.jsx';
import RecurrencePicker, { buildRRuleClient, parseRRuleClient } from '../components/RecurrencePicker.jsx';

/**
 * Sprint 4 — Vue "Mes congés".
 *
 * Affiche les demandes du user courant (Person liée par email). Permet :
 *  - Déclarer un temps partiel (#86 — seul type créable ici, validé d'office
 *    par le serveur ; les autres congés se déposent dans Gescom, #64).
 *  - Sous-traitant : déclarer une indisponibilité (validée d'office, hors
 *    réglage self-service, comme dans le planning).
 *  - Modifier une demande tant qu'elle est PENDING.
 *  - Supprimer une demande PENDING, ou un temps partiel quel que soit son
 *    statut (pour le recréer ailleurs).
 *  - Lecture seule sur le reste (congés importés de Gescom).
 */

// #86 — miroir de `TIMEOFF_TYPE_NOT_IN_EXPORT` côté API, qui reste l'autorité.
const PART_TIME_TYPE_NAME = 'Temps Partiel';
const UNAVAILABILITY_TYPE_NAME = 'Indisponibilité';

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 14,
};

// #20 — un <select> et ses <option> rendus avec un fond translucide/hérité du
// système deviennent illisibles (texte clair sur fond clair). On force un fond
// opaque + une couleur de police contrastée (variables thème).
const selectStyle = {
  ...inputStyle,
  background: 'var(--card-bg)',
  color: 'var(--text)',
};
const optionStyle = { background: 'var(--card-bg)', color: 'var(--text)' };

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const secondaryBtn = {
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 14px',
  fontSize: 13,
  cursor: 'pointer',
};

const STATUS_PILL = {
  PENDING: { bg: 'var(--warning)', label: 'En attente' },
  APPROVED: { bg: 'var(--success)', label: 'Approuvé' },
  REJECTED: { bg: 'var(--danger)', label: 'Refusé' },
};

function toDateInput(iso) {
  if (!iso) return '';
  return new Date(iso).toISOString().slice(0, 10);
}

/** Sprint 5 — compte jours ouvrés (hors week-ends et fériés) sur [start, end]. */
function countWorkingDays(startIso, endIso, holidaySet) {
  if (!startIso || !endIso) return 0;
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  let count = 0;
  // Itère jour par jour en UTC pour éviter les sauts DST.
  const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
  const last = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));
  while (cursor <= last) {
    const day = cursor.getUTCDay(); // 0=Dim 6=Sam
    const iso = cursor.toISOString().slice(0, 10);
    if (day !== 0 && day !== 6 && !holidaySet.has(iso)) count += 1;
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return count;
}

/** Jours calendaires inclusifs entre deux dates ISO. */
function countCalendarDays(startIso, endIso) {
  if (!startIso || !endIso) return 0;
  const start = new Date(startIso);
  const end = new Date(endIso);
  const ms = end.getTime() - start.getTime();
  if (!Number.isFinite(ms) || ms < 0) return 0;
  return Math.round(ms / (24 * 60 * 60 * 1000)) + 1;
}

// ---------- #21/#22 — Vue calendaire des congés (occurrences expansées) ----------
const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const DOW_FR = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'];

function isoUTC(y, m, d) {
  return new Date(Date.UTC(y, m, d)).toISOString().slice(0, 10);
}

/**
 * Calendrier mensuel des congés de l'utilisateur. Réutilise l'expansion serveur
 * de `/api/schedule` (instances avec `occurrenceDate` + `personIds`) → les
 * occurrences récurrentes (ex. Temps Partiel hebdo) du mois apparaissent bien
 * (corrige #22), contrairement à la liste qui ne montre que la série d'origine.
 */
function CongesCalendar({ myPersonId }) {
  const today = new Date();
  const [cursor, setCursor] = useState({ y: today.getUTCFullYear(), m: today.getUTCMonth() });
  const [byDay, setByDay] = useState(new Map());
  const [loading, setLoading] = useState(true);

  // Grille : du lundi avant le 1er au dimanche après le dernier jour du mois.
  const grid = useMemo(() => {
    const first = new Date(Date.UTC(cursor.y, cursor.m, 1));
    const startOffset = (first.getUTCDay() + 6) % 7; // 0 = lundi
    const gridStart = new Date(Date.UTC(cursor.y, cursor.m, 1 - startOffset));
    const days = [];
    for (let i = 0; i < 42; i += 1) {
      const d = new Date(gridStart.getTime() + i * 86400000);
      days.push(d);
    }
    return { days, from: days[0], to: days[41] };
  }, [cursor]);

  useEffect(() => {
    if (!myPersonId) return undefined;
    let cancelled = false;
    setLoading(true);
    apiSchedule
      .get({ from: grid.from.toISOString().slice(0, 10), to: grid.to.toISOString().slice(0, 10) })
      .then((data) => {
        if (cancelled) return;
        const map = new Map();
        for (const t of data?.timeOffs ?? []) {
          if (!(t.personIds ?? []).includes(myPersonId)) continue;
          // L'instance couvre [startDate, endDate] ; on marque chaque jour.
          const s = new Date(t.startDate + 'T00:00:00Z');
          const e = new Date((t.endDate ?? t.startDate) + 'T00:00:00Z');
          for (let d = new Date(s); d <= e; d = new Date(d.getTime() + 86400000)) {
            const iso = d.toISOString().slice(0, 10);
            if (!map.has(iso)) map.set(iso, []);
            map.get(iso).push(t);
          }
        }
        setByDay(map);
      })
      .catch(() => !cancelled && setByDay(new Map()))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [grid.from, grid.to, myPersonId]);

  const shift = (delta) =>
    setCursor((c) => {
      const m = c.m + delta;
      return { y: c.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 };
    });

  const todayIso = today.toISOString().slice(0, 10);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <button type="button" onClick={() => shift(-1)} style={secondaryBtn} title="Mois précédent">
          <ChevronLeft size={16} />
        </button>
        <div style={{ fontWeight: 600, minWidth: 160, textAlign: 'center', color: 'var(--text)' }}>
          {MONTHS_FR[cursor.m]} {cursor.y}
        </div>
        <button type="button" onClick={() => shift(1)} style={secondaryBtn} title="Mois suivant">
          <ChevronRight size={16} />
        </button>
        {loading ? <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Chargement…</span> : null}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
        {DOW_FR.map((d) => (
          <div key={d} style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, padding: '4px 0' }}>
            {d}
          </div>
        ))}
        {grid.days.map((d) => {
          const iso = d.toISOString().slice(0, 10);
          const inMonth = d.getUTCMonth() === cursor.m;
          const items = byDay.get(iso) ?? [];
          const isToday = iso === todayIso;
          return (
            <div
              key={iso}
              style={{
                minHeight: 78,
                border: `1px solid ${isToday ? 'var(--accent)' : 'var(--glass-border)'}`,
                borderRadius: 'var(--radius-sm)',
                background: inMonth ? 'var(--card-bg)' : 'var(--glass)',
                opacity: inMonth ? 1 : 0.5,
                padding: 4,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right' }}>{d.getUTCDate()}</div>
              {items.map((t, idx) => (
                <div
                  key={`${t.instanceId ?? t.id}-${idx}`}
                  title={`${t.timeOffType?.name ?? 'Absence'} (${timeOffDurationLabel(t)})`}
                  style={{
                    fontSize: 10,
                    color: '#fff',
                    background: t.timeOffType?.colorHex || 'var(--warning)',
                    borderRadius: 4,
                    padding: '1px 4px',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {t.timeOffType?.name ?? 'Absence'}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function MyTimeOffsView({ user }) {
  const [myPerson, setMyPerson] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'calendar' (#21)
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sprint 11 — la dépose de congés en self-service peut être désactivée par un
  // admin (congés poussés par une app tierce via l'API publique). Default true
  // pour ne pas masquer la fonctionnalité tant que la valeur n'est pas chargée.
  const isAdmin = user?.role === 'ADMIN';
  const [selfEnabled, setSelfEnabled] = useState(true);
  // Les ADMIN ne sont jamais bloqués par le flag (cohérent backend).
  const canSelfManage = isAdmin || selfEnabled;
  // Sous-traitant : ses indisponibilités ne relèvent pas du réglage
  // self-service des congés (le serveur applique la même règle).
  const isContractor = myPerson?.employeeType === 'CONTRACTOR' && user?.role !== 'VIEWER';
  // Temps partiel : réservé aux personnes marquées sur leur fiche (l'ADMIN
  // garde la main, comme côté serveur).
  const canDeclarePartTime =
    user?.role !== 'VIEWER' && canSelfManage && (isAdmin || Boolean(myPerson?.partTime));
  const canDeclare = canDeclarePartTime || isContractor;

  // Sprint 5 — Set d'ISO dates fériés couvrant toutes les demandes affichées.
  const [holidaySet, setHolidaySet] = useState(new Set());

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    timeOffTypeId: '',
    startDate: '',
    endDate: '',
    hoursPerDay: 8,
    fullDay: true,
    startHalf: 'AM',
    endHalf: 'PM',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  // ---- Charge la Person liée au user courant (par email lowercase) ----
  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ items: personsAll }, { items: itemsAll }, { items: typesAll }, appSettings] =
        await Promise.all([
          apiPersons.list('active'),
          // #21 — 'active' (pas 'all') : sinon un congé supprimé (archivé) reste
          // affiché dans la liste (« j'ai supprimé mais il est toujours là »).
          apiTimeOffs.list('active'),
          apiTimeOffTypes.list('active'),
          apiSettings.get().catch(() => ({ selfTimeOffRequestEnabled: true })),
        ]);
      setSelfEnabled(appSettings?.selfTimeOffRequestEnabled !== false);
      const myEmail = (user?.email ?? '').toLowerCase();
      const me = personsAll.find((p) => (p.email ?? '').toLowerCase() === myEmail);
      setMyPerson(me ?? null);
      // #86 — seul le temps partiel se déclare ici, plus l'indisponibilité pour
      // un sous-traitant (proposée en premier : c'est son cas d'usage). Chaque
      // type n'apparaît que si le serveur l'accepterait.
      const selfOk =
        user?.role !== 'VIEWER' &&
        (user?.role === 'ADMIN' ||
          (appSettings?.selfTimeOffRequestEnabled !== false && Boolean(me?.partTime)));
      const contractor = me?.employeeType === 'CONTRACTOR' && user?.role !== 'VIEWER';
      setTypes([
        ...(contractor ? typesAll.filter((t) => t.name === UNAVAILABILITY_TYPE_NAME) : []),
        ...(selfOk ? typesAll.filter((t) => t.name === PART_TIME_TYPE_NAME) : []),
      ]);
      // Filtre côté front : ne garder que les demandes incluant ma Person.
      const mine = me
        ? itemsAll.filter((t) =>
            (t.persons ?? []).some((p) => p.personId === me.id),
          )
        : [];
      setItems(mine);

      // Sprint 5 — charge les fériés couvrant la plage des demandes.
      if (me && mine.length > 0) {
        const minDate = mine.reduce(
          (m, t) => (t.startDate && (!m || t.startDate < m) ? t.startDate : m),
          null,
        );
        const maxDate = mine.reduce(
          (m, t) => (t.endDate && (!m || t.endDate > m) ? t.endDate : m),
          null,
        );
        if (minDate && maxDate) {
          try {
            const { items: hols } = await apiPublicHolidays.forPerson(
              me.id,
              toDateInput(minDate),
              toDateInput(maxDate),
            );
            const next = new Set();
            for (const h of hols ?? []) {
              const iso = (h.date ?? '').slice(0, 10);
              if (iso) next.add(iso);
            }
            setHolidaySet(next);
          } catch {
            setHolidaySet(new Set());
          }
        } else {
          setHolidaySet(new Set());
        }
      } else {
        setHolidaySet(new Set());
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Sprint 8 — récurrence et dialog de scope.
  const [recurrence, setRecurrence] = useState({ freq: 'none' });
  const [scopeDialog, setScopeDialog] = useState(null); // { mode, payload? }

  const openCreate = () => {
    setEditing(null);
    setForm({
      timeOffTypeId: types[0]?.id ?? '',
      startDate: '',
      endDate: '',
      hoursPerDay: 8,
      fullDay: true,
      startHalf: 'AM',
      endHalf: 'PM',
      notes: '',
    });
    setRecurrence({ freq: 'none' });
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      timeOffTypeId: t.timeOffTypeId,
      startDate: toDateInput(t.startDate),
      endDate: toDateInput(t.endDate),
      hoursPerDay: Number(t.hoursPerDay ?? 0),
      fullDay: Boolean(t.fullDay),
      startHalf: t.startHalf ?? 'AM',
      endHalf: t.endHalf ?? 'PM',
      notes: t.notes ?? '',
    });
    setRecurrence(parseRRuleClient(t.recurrenceRule ?? null));
    setFormError(null);
    setModalOpen(true);
  };

  const buildPayload = () => ({
    timeOffTypeId: form.timeOffTypeId,
    startDate: form.startDate,
    endDate: form.endDate,
    hoursPerDay: form.fullDay ? 0 : Number(form.hoursPerDay),
    fullDay: form.fullDay,
    // Les demi-journées n'ont de sens que sur une journée pleine : en durée
    // libre, c'est `hoursPerDay` qui porte l'information (contrainte API).
    startHalf: form.fullDay ? form.startHalf : 'AM',
    endHalf: form.fullDay ? form.endHalf : 'PM',
    notes: form.notes.trim() || null,
    // Sprint 8 — RRULE optionnelle.
    recurrenceRule: buildRRuleClient(recurrence),
  });

  // Sprint 8.3 — Pour `this_occurrence`, permettre le déplacement/redim
  // d'une occurrence individuelle via movedToDate + durationDays.
  const buildInstancePayload = () => {
    const originalOccDate =
      editing?.occurrenceDate ??
      (editing?.startDate ? editing.startDate.slice(0, 10) : form.startDate);
    const payload = {
      hoursPerDay: form.fullDay ? 0 : Number(form.hoursPerDay),
      fullDay: form.fullDay,
      startHalf: form.fullDay ? form.startHalf : 'AM',
      endHalf: form.fullDay ? form.endHalf : 'PM',
      notes: form.notes.trim() || null,
    };
    if (form.startDate && form.startDate !== originalOccDate) {
      payload.movedToDate = form.startDate;
    }
    if (form.startDate && form.endDate && form.endDate >= form.startDate) {
      const days = Math.round(
        (new Date(form.endDate).getTime() - new Date(form.startDate).getTime()) / 86_400_000,
      ) + 1;
      if (days >= 1) payload.durationDays = days;
    }
    return payload;
  };

  const performSave = async (scope) => {
    setSubmitting(true);
    setFormError(null);
    try {
      if (!editing) {
        await apiTimeOffs.create({ ...buildPayload(), personIds: [myPerson.id] });
      } else if (!scope || scope === 'all_occurrences') {
        await apiTimeOffs.update(editing.id, buildPayload());
      } else {
        await apiTimeOffs.patchInstance(
          editing.id,
          editing.occurrenceDate ?? editing.startDate.slice(0, 10),
          scope,
          buildInstancePayload(),
        );
      }
      setModalOpen(false);
      await loadAll();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!myPerson) {
      setFormError('Aucune Person liée à votre compte');
      return;
    }
    // Sprint 8 — si la demande existante est récurrente, demander le scope.
    if (editing && editing.recurrenceRule) {
      setScopeDialog({ mode: 'edit' });
      return;
    }
    await performSave(null);
  };

  const onDelete = async (t) => {
    if (t.recurrenceRule) {
      // Sprint 8 — récurrent : EditScopeDialog
      setEditing(t);
      setScopeDialog({ mode: 'delete' });
      return;
    }
    if (!window.confirm('Supprimer cette déclaration ?')) return;
    try {
      await apiTimeOffs.archive(t.id);
      await loadAll();
    } catch (e) {
      setError(e.message);
    }
  };

  const performDelete = async (scope) => {
    if (!editing) return;
    try {
      if (!scope || scope === 'all_occurrences') {
        await apiTimeOffs.archive(editing.id);
      } else {
        await apiTimeOffs.deleteInstance(
          editing.id,
          editing.occurrenceDate ?? editing.startDate.slice(0, 10),
          scope,
        );
      }
      await loadAll();
    } catch (e) {
      setError(e.message);
    }
  };

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => (b.startDate ?? '').localeCompare(a.startDate ?? '')),
    [items],
  );

  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Chargement…</div>;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          background: 'var(--bg)',
          paddingBottom: 12,
          zIndex: 5,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--text)' }}>Mes congés</h1>
        {/* #21 — bascule Liste / Calendrier */}
        <div style={{ display: 'inline-flex', gap: 4 }}>
          {[
            { key: 'list', label: 'Liste', Icon: List },
            { key: 'calendar', label: 'Calendrier', Icon: CalendarDays },
          ].map(({ key, label, Icon }) => {
            const active = viewMode === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setViewMode(key)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  borderRadius: 'var(--radius-pill)',
                  border: `1px solid ${active ? 'var(--accent)' : 'var(--glass-border)'}`,
                  background: active ? 'var(--accent)' : 'transparent',
                  color: active ? '#fff' : 'var(--text)',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                <Icon size={14} /> {label}
              </button>
            );
          })}
        </div>
        {canDeclare ? (
          <button
            type="button"
            onClick={openCreate}
            disabled={!myPerson}
            style={{ ...primaryBtn, marginLeft: 'auto', opacity: myPerson ? 1 : 0.5 }}
          >
            <Plus size={14} /> {isContractor ? 'Déclarer une absence' : 'Déclarer un temps partiel'}
          </button>
        ) : null}
      </div>

      {!canSelfManage && !isContractor ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 12,
            padding: '10px 14px',
            background: 'var(--glass)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--text-muted)',
            fontSize: 13,
          }}
        >
          <Lock size={16} style={{ flexShrink: 0 }} />
          La dépose de congés en self-service est désactivée. Vos congés sont gérés via une
          application externe ; cette page reste consultable mais en lecture seule.
        </div>
      ) : null}

      {canSelfManage || isContractor ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 12,
            padding: '10px 14px',
            background: 'var(--glass)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--text-muted)',
            fontSize: 13,
          }}
        >
          <Info size={16} style={{ flexShrink: 0 }} />
          {isContractor
            ? "En tant que sous-traitant, vous déclarez ici vos indisponibilités : elles sont validées automatiquement, retirent ces jours du planning, et vous pouvez les supprimer à tout moment."
            : canDeclarePartTime
            ? 'Les congés se déposent dans Gescom et apparaissent ici après import. Seul le temps partiel se déclare dans Synolog : il est validé automatiquement, et vous pouvez le supprimer pour le recréer ailleurs.'
            : "Les congés se déposent dans Gescom et apparaissent ici après import. La déclaration d'un temps partiel est réservée aux personnes dont la fiche le prévoit : voyez avec un administrateur si c'est votre cas."}
        </div>
      ) : null}

      {!myPerson ? (
        <div style={{ color: 'var(--warning)', marginBottom: 12 }}>
          Aucune Person liée à votre compte ({user?.email}). Demandez à un administrateur de
          créer votre fiche Person avec cet email.
        </div>
      ) : null}

      {error ? <div style={{ color: 'var(--danger)', marginBottom: 12 }}>{error}</div> : null}

      {viewMode === 'calendar' ? (
        <CongesCalendar myPersonId={myPerson?.id ?? null} />
      ) : (
      <div
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'auto',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--glass)', textAlign: 'left' }}>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Type</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Période</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Jours</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>h/jour</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Statut</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Note manager</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500, width: 110 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: 20, color: 'var(--text-muted)' }}>
                  Aucune demande pour le moment.
                </td>
              </tr>
            ) : (
              sortedItems.map((t) => {
                const pill = STATUS_PILL[t.status] ?? STATUS_PILL.PENDING;
                const isPartTime = t.timeOffType?.name === PART_TIME_TYPE_NAME;
                const isOwnUnavailability =
                  isContractor && !t.archivedAt && t.timeOffType?.name === UNAVAILABILITY_TYPE_NAME;
                // Aligné sur le serveur (audit #86) : un congé importé de Gescom
                // se gère dans Gescom (F-86-02), et seul un type propre à Synolog
                // reste modifiable (F-86-01). Le serveur revérifie de toute façon.
                const pendingOwn =
                  t.status === 'PENDING' && !t.archivedAt && canSelfManage && !t.gescomAbsenceId;
                const editable = pendingOwn && isPartTime;
                // #86 — un temps partiel (validé d'office) reste supprimable par
                // son auteur pour être recréé ailleurs.
                const deletable =
                  pendingOwn || (canSelfManage && !t.archivedAt && isPartTime) || isOwnUnavailability;
                const gescomUrl = gescomRequestUrl(t.gescomAbsenceId);
                return (
                  <tr key={t.id} style={{ borderTop: '1px solid var(--glass-border)' }}>
                    <td style={{ padding: '10px 12px', color: 'var(--text)' }}>
                      {t.timeOffType?.name ?? '—'}
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>
                      {toDateInput(t.startDate)} → {toDateInput(t.endDate)}
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-muted)', fontSize: 12 }}>
                      {(() => {
                        const startIso = toDateInput(t.startDate);
                        const endIso = toDateInput(t.endDate);
                        const calendar = countCalendarDays(startIso, endIso);
                        const working = countWorkingDays(startIso, endIso, holidaySet);
                        return (
                          <>
                            <strong style={{ color: 'var(--text)' }}>{working}</strong> ouvré{working > 1 ? 's' : ''}
                            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                              ({calendar} calendaire{calendar > 1 ? 's' : ''})
                            </div>
                          </>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>
                      {timeOffDurationLabel(t)}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 10px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 500,
                          background: pill.bg,
                          color: '#fff',
                        }}
                      >
                        {pill.label}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-muted)', maxWidth: 280 }}>
                      {t.statusNote ?? '—'}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {deletable || gescomUrl ? (
                        <div style={{ display: 'inline-flex', gap: 6 }}>
                          {editable ? (
                          <button
                            type="button"
                            onClick={() => openEdit(t)}
                            title="Modifier"
                            style={{
                              background: 'transparent',
                              border: '1px solid var(--glass-border)',
                              borderRadius: 'var(--radius-sm)',
                              padding: 6,
                              color: 'var(--text)',
                              cursor: 'pointer',
                            }}
                          >
                            <Pencil size={14} />
                          </button>
                          ) : null}
                          {gescomUrl ? (
                            <a
                              href={gescomUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Voir la demande dans Gescom"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                background: 'transparent',
                                border: '1px solid var(--glass-border)',
                                borderRadius: 'var(--radius-sm)',
                                padding: 6,
                                color: 'var(--accent)',
                              }}
                            >
                              <ExternalLink size={14} />
                            </a>
                          ) : null}
                          {deletable ? (
                          <button
                            type="button"
                            onClick={() => onDelete(t)}
                            title="Supprimer"
                            style={{
                              background: 'transparent',
                              border: '1px solid var(--glass-border)',
                              borderRadius: 'var(--radius-sm)',
                              padding: 6,
                              color: 'var(--danger)',
                              cursor: 'pointer',
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                          ) : null}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={
          editing
            ? 'Modifier la demande'
            : isContractor
            ? 'Déclarer une absence'
            : 'Déclarer un temps partiel'
        }
        footer={
          <>
            <button type="button" onClick={() => setModalOpen(false)} style={secondaryBtn}>
              Annuler
            </button>
            <button
              type="submit"
              form="my-timeoff-form"
              disabled={
                submitting ||
                !form.timeOffTypeId ||
                !form.startDate ||
                !form.endDate
              }
              style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}
            >
              Enregistrer
            </button>
          </>
        }
      >
        <form id="my-timeoff-form" onSubmit={onSubmit} style={{ display: 'grid', gap: 12 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              Type d'absence
            </label>
            {types.length === 0 ? (
              <div style={{ color: 'var(--warning)', fontSize: 12, marginBottom: 6 }}>
                {isContractor
                  ? "Le type « Indisponibilité » n'est pas configuré — contactez un administrateur."
                  : "Le type « Temps Partiel » n'est pas configuré — contactez un administrateur."}
              </div>
            ) : null}
            <select
              value={form.timeOffTypeId}
              onChange={(e) => setForm({ ...form, timeOffTypeId: e.target.value })}
              required
              style={selectStyle}
            >
              <option value="" style={optionStyle}>— Sélectionner un type —</option>
              {types.map((t) => (
                <option key={t.id} value={t.id} style={optionStyle}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
                Date de début
              </label>
              <DatePicker
                value={form.startDate}
                onChange={(v) => setForm({ ...form, startDate: v })}
                required
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
                Date de fin
              </label>
              <DatePicker
                value={form.endDate}
                onChange={(v) => setForm({ ...form, endDate: v })}
                required
                style={inputStyle}
              />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text)' }}>
              <input
                type="checkbox"
                checked={form.fullDay}
                onChange={(e) => setForm({ ...form, fullDay: e.target.checked })}
              />
              Jour entier
            </label>
            {!form.fullDay ? (
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--text-muted)' }}>
                  Heures par jour
                </label>
                <input
                  type="number"
                  min="0"
                  max="24"
                  step="0.5"
                  value={form.hoursPerDay}
                  onChange={(e) => setForm({ ...form, hoursPerDay: Number(e.target.value) })}
                  style={inputStyle}
                />
              </div>
            ) : null}
          </div>
          {form.fullDay ? (
            <HalfDayPicker
              startHalf={form.startHalf}
              endHalf={form.endHalf}
              onChange={(halves) => setForm({ ...form, ...halves })}
              sameDay={Boolean(form.startDate) && form.startDate === form.endDate}
            />
          ) : null}
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              Notes (optionnel)
            </label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              maxLength={5000}
              style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
            />
          </div>
          {/* Sprint 8 — récurrence (optionnelle) */}
          <div
            style={{
              borderTop: '1px solid var(--glass-border)',
              paddingTop: 12,
              marginTop: 4,
            }}
          >
            <RecurrencePicker value={recurrence} onChange={setRecurrence} disabled={submitting} />
            {editing?.recurrenceRule ? (
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                <Repeat size={11} style={{ verticalAlign: '-1px' }} /> Cette demande est récurrente
                — le périmètre vous sera demandé à l'enregistrement.
              </p>
            ) : null}
          </div>
          {formError ? <div style={{ color: 'var(--danger)', fontSize: 13 }}>{formError}</div> : null}
        </form>
      </Modal>

      <EditScopeDialog
        open={scopeDialog !== null}
        mode={scopeDialog?.mode ?? 'edit'}
        entityLabel="congé"
        onCancel={() => setScopeDialog(null)}
        onConfirm={async (scope) => {
          const mode = scopeDialog?.mode;
          setScopeDialog(null);
          if (mode === 'delete') await performDelete(scope);
          else await performSave(scope);
        }}
      />
    </div>
  );
}
